import { Card, CardHeader, CardTitle, CardContent, Empty, Badge } from 'prosci-ui';

export interface GuidedFunnelRow {
  use_case_key: string;
  viewed: number;
  started: number;
  drafted: number;
  completed: number;
  median_duration_s: number | null;
}

export interface GuidedMilestoneRow {
  use_case_key: string;
  slot: string;
  met: number;
  nudge: number;
  revisited: number;
}

export interface GuidedUseCaseStats {
  window_days: number;
  funnel: GuidedFunnelRow[];
  milestones: GuidedMilestoneRow[];
  trust: { manual_edits: Record<string, number>; rewinds: Record<string, number> };
  value: {
    exports_by_format: Record<string, number>;
    shares: number;
    share_views: number;
    shares_revoked: number;
  };
  friction: {
    tier_limit_hits: number;
    uploads_rejected: number;
    turn_failures: number;
  };
}

function duration(seconds: number | null) {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function rate(numerator: number, denominator: number) {
  if (!denominator) return '—';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

function CountList({ counts }: { counts: Record<string, number> }) {
  const entries = Object.entries(counts || {}).filter(([key]) => key);
  if (!entries.length) return <p className="text-sm text-muted-foreground">None yet</p>;
  return (
    <ul className="text-sm space-y-1">
      {entries.map(([key, value]) => (
        <li key={key} className="flex justify-between gap-4">
          <span className="text-muted-foreground">{key}</span>
          <span className="font-medium">{value}</span>
        </li>
      ))}
    </ul>
  );
}

export function GuidedUseCasesSection({ stats }: { stats?: GuidedUseCaseStats }) {
  if (!stats) return null;

  const { funnel, milestones, trust, value, friction } = stats;

  return (
    <div className="mt-8 space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-medium">Guided Use Cases</h2>
        <Badge variant="outline">last {stats.window_days} days</Badge>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Adoption funnel</CardTitle>
        </CardHeader>
        <CardContent>
          {funnel.length === 0 ? (
            <Empty description="No guided activity in this window." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2">Canvas</th>
                    <th className="py-2">Viewed</th>
                    <th className="py-2">Started</th>
                    <th className="py-2">Drafted</th>
                    <th className="py-2">Completed</th>
                    <th className="py-2">Start rate</th>
                    <th className="py-2">Finish rate</th>
                    <th className="py-2">Median time</th>
                  </tr>
                </thead>
                <tbody>
                  {funnel.map((row) => (
                    <tr key={row.use_case_key} className="border-t">
                      <td className="py-2 font-medium">{row.use_case_key}</td>
                      <td className="py-2">{row.viewed}</td>
                      <td className="py-2">{row.started}</td>
                      <td className="py-2">{row.drafted}</td>
                      <td className="py-2">{row.completed}</td>
                      <td className="py-2">{rate(row.started, row.viewed)}</td>
                      <td className="py-2">{rate(row.completed, row.started)}</td>
                      <td className="py-2">{duration(row.median_duration_s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Per-section drop-off</CardTitle>
        </CardHeader>
        <CardContent>
          {milestones.length === 0 ? (
            <Empty description="No sections completed yet." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2">Canvas</th>
                    <th className="py-2">Section</th>
                    <th className="py-2">Met</th>
                    <th className="py-2">Could be stronger</th>
                    <th className="py-2">Revisited</th>
                  </tr>
                </thead>
                <tbody>
                  {milestones.map((row) => (
                    <tr key={`${row.use_case_key}-${row.slot}`} className="border-t">
                      <td className="py-2">{row.use_case_key}</td>
                      <td className="py-2 font-medium">{row.slot}</td>
                      <td className="py-2">{row.met}</td>
                      <td className="py-2">{row.nudge}</td>
                      <td className="py-2">{row.revisited}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Trust signals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Overriding a draft or rewinding a turn is stronger evidence than a
                thumbs-up widget, and both endpoints already existed. */}
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Manual edits</p>
              <CountList counts={trust.manual_edits} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Rewinds</p>
              <CountList counts={trust.rewinds} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Value realization</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Exports by format</p>
              <CountList counts={value.exports_by_format} />
            </div>
            <CountList
              counts={{
                shares: value.shares,
                'share views': value.share_views,
                revoked: value.shares_revoked,
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Friction</CardTitle>
          </CardHeader>
          <CardContent>
            <CountList
              counts={{
                'tier limit hits': friction.tier_limit_hits,
                'uploads rejected': friction.uploads_rejected,
                'turn failures': friction.turn_failures,
              }}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
